// src/hooks/useRealtimeRefetch.ts
//
// Shared live-update hook: subscribe to postgres_changes on one table and run a
// (silent) refetch whenever a row changes. Originally lifted from the pattern
// inside the legacy useResolutions.ts so that Epic 4's several consumers (4.1's
// ticket views, 4.2's detail, 4.8's failure view, 4.9's bell) share ONE
// implementation. That legacy hook was deleted in Story 4.10 (it kept its own
// inline copy); this file is now the only implementation.
//
// The callback MUST be a silent refetch — no loading spinner flash, no focus
// stealing. Events respect RLS (tables are in the supabase_realtime publication
// per Story 3.5). The channel is torn down on cleanup (removeChannel), so a
// remount or table change never leaks a subscription.
//
// The optional `channelKey` (Story 4.9) disambiguates the Supabase channel name
// when TWO consumers subscribe to the SAME table on the same page. The channel is
// named `yak-live-<table>` by default; 4.8's useFailures and 4.9's always-mounted
// bell both subscribe to `yak_notifications`, so on /settings/failures they would
// otherwise create two same-named channels. Passing a distinct channelKey (e.g.
// the bell passes 'bell') keeps them separate. Existing call sites omit it and are
// unchanged.

import { useEffect } from 'react'
import { supabase } from '../lib/supabase'

export function useRealtimeRefetch(
  table: string,
  refetch: () => void,
  channelKey?: string
): void {
  useEffect(() => {
    const channelName = channelKey
      ? `yak-live-${table}-${channelKey}`
      : `yak-live-${table}`
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table }, () =>
        refetch()
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }, [table, refetch, channelKey])
}
